import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { Form, Input, Typography, Alert } from 'antd';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/Button';
import { PasswordStrength } from '@/components/PasswordStrength';
import { ROUTES } from '@/routes';
import { validatePassword, validatePasswordsMatch, parseClerkError } from '@/utils';
import styles from './ResetPasswordConfirm.module.scss';

interface ResetPasswordConfirmFormValues {
  code: string;
  password: string;
  confirmPassword: string;
}

interface LocationState {
  email?: string;
}

export const ResetPasswordConfirm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, setActive, isLoaded } = useSignIn();

  const state = location.state as LocationState | null;
  const email = state?.email;

  const [form] = Form.useForm<ResetPasswordConfirmFormValues>();
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: ResetPasswordConfirmFormValues) => {
    if (!signIn || !isLoaded) return;

    const passwordValidation = validatePassword(values.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return;
    }

    if (!validatePasswordsMatch(values.password, values.confirmPassword)) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: values.code,
        password: values.password,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate(ROUTES.SESSIONS, { replace: true });
      } else {
        console.warn('Password reset requires additional steps:', result.status);
        setError('Password reset requires additional verification. Please contact support.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError(parseClerkError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className={styles.container}>
      <button
        type="button"
        onClick={handleBack}
        className={styles.backButton}
        aria-label="Go back"
      >
        <ArrowLeft style={{ width: '1em', height: '1em' }} /> Back
      </button>

      <Typography.Title level={1} className={styles.title}>
        Set new password
      </Typography.Title>
      <Typography.Text className={styles.subtitle}>
        {email ? (
          <>We sent a code to <strong>{email}</strong>. Enter it below with your new password.</>
        ) : (
          'Enter the code we sent to your email along with your new password.'
        )}
      </Typography.Text>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={styles.alert}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
        colon={false}
        className={styles.form}
      >
        <Form.Item
          name="code"
          label="Reset code"
          rules={[{ required: true, message: 'Please enter the reset code' }]}
        >
          <Input.OTP length={6} size="large" disabled={isSubmitting} />
        </Form.Item>

        <Form.Item
          name="password"
          label="New password"
          rules={[
            { required: true, message: 'Please enter your new password' },
            { min: 8, message: 'Password must be at least 8 characters' },
          ]}
        >
          <Input.Password
            placeholder="At least 8 characters"
            size="large"
            autoComplete="new-password"
            disabled={isSubmitting}
            onChange={(e) => setPassword(e.target.value)}
            iconRender={(visible) => (visible ? 'Hide' : 'Show')}
          />
        </Form.Item>
        <PasswordStrength value={password} />

        <Form.Item
          name="confirmPassword"
          label="Confirm new password"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Please confirm your new password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Type it again"
            size="large"
            autoComplete="new-password"
            disabled={isSubmitting}
            iconRender={(visible) => (visible ? 'Hide' : 'Show')}
          />
        </Form.Item>

        <Button appearance="solid" htmlType="submit" size="large" block loading={isSubmitting} disabled={!isLoaded}>
          Update password
        </Button>
      </Form>

      <div className={styles.footer}>
        <Typography.Text type="secondary">
          Didn't receive a code?{' '}
          <Link to={ROUTES.RESET_PASSWORD}>Try again</Link>
        </Typography.Text>
      </div>
    </div>
  );
};

export default ResetPasswordConfirm;
